// @flow

import React, {Component, createRef} from 'react';
import {DraggableCore} from 'react-draggable';
import detectPassiveEvents from 'detect-passive-events';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';
import {shouldUpdate} from '@pb/kit-util-should-update';

import styles from './scrollable.component.module.styl';
import {Direction, Style, SETTINGS} from './constants';
import {exceedGap} from './utils/exceedGap';

import type {DraggableData} from 'react-draggable';

import type {DirectionType, StyleType} from './constants';
import type {OnWheelHandler} from './typedef';

const hasPassiveEventSupport = detectPassiveEvents.hasSupport;
const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

type Props = {|
    id?: string,
    type: StyleType,
    show: boolean,
    direction: DirectionType,
    containerSize: number,
    contentSize: number,
    shift: number,
    onWheel?: OnWheelHandler,
    updateData: (deltaX: number, dragging: boolean) => void,
    getRef: (el: ?HTMLDivElement) => void,
|};

type State = {|
    lineSize: number,
|};

const STYLE_CLASSES: { [key: StyleType]: string, } = {
    [Style.LIGHT]: 'scroll_light',
    [Style.DARK]: 'scroll_dark'
};

const SCROLL_HANDLE_MIN_SIZE = 32;

/**
 *  @deprecated It will be removed in 6 months 09-15-2025. use `@pb/kit-component-native-scroll`
 *  */
export class ScrollLine extends Component<Props, State> {
    scrollLine: ?HTMLDivElement;
    nodeRef = createRef<?HTMLDivElement>();

    static defaultProps = {
        getRef: () => undefined,
        show: true
    };

    state = {
        lineSize: 0
    };

    componentDidMount() {
        this.props.getRef(this.scrollLine);
        this.addListeners();
        this.updateLineSize();
    }

    componentDidUpdate(prevProps: Props) {
        if (shouldUpdate(prevProps, this.props)) {
            this.updateLineSize();
        }
    }

    componentWillUnmount() {
        this.removeListeners();

        // return back to scrollLineContainer when ScrollLine was in custom place
        const {id} = this.props;
        if (id) {
            const scrollLineContainer = document.getElementById(id);
            if (scrollLineContainer && this.scrollLine) {
                scrollLineContainer.appendChild(this.scrollLine);
            }
        }
    }

    addListeners() {
        if (this.scrollLine && this.props.onWheel) {
            this.scrollLine.addEventListener('wheel', this.onWheel, hasPassiveEventSupport ? {passive: false} : false);
        }
    }

    removeListeners() {
        if (this.scrollLine) {
            this.scrollLine.removeEventListener('wheel', this.onWheel);
        }
    }

    onWheel: OnWheelHandler = e => {
        if (this.props.onWheel) {
            this.props.onWheel(e);
        }
    };

    updateLineSize() {
        if (this.scrollLine) {
            const {lineSize} = this.state;
            const {direction} = this.props;
            const {size} = SETTINGS[direction];
            // $FlowFixMe TODO: computed property. Indexable signature not found in
            const lineSizeBounding = Number(this.scrollLine.getBoundingClientRect()[size]);

            if (!isNaN(lineSizeBounding) && !isNaN(lineSize) && exceedGap(lineSizeBounding, lineSize)) {
                this.setState({lineSize: lineSizeBounding});
            }
        }
    }

    onStart = (e: Event) => {
        e.stopPropagation();
    };

    onDrag = (e: Event, data: DraggableData) => {
        e.stopPropagation();
        const {direction} = this.props;
        const {axis, delta, lastAxis} = SETTINGS[direction];
        if (data[axis] !== data[lastAxis]) {
            this.onUpdateData(data[delta], true);
        }
    };

    onStop = (e: Event, data: DraggableData) => {
        e.stopPropagation();
        const {direction} = this.props;
        const {delta} = SETTINGS[direction];
        setTimeout(() => {
            this.onUpdateData(data[delta], false);
        }, 0);
    };

    onHandleClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
    };

    onLineClick = (e: Event) => {
        if (this.scrollLine) {
            const {direction} = this.props;
            const {pageAxis, indent} = SETTINGS[direction];
            const {handleSize, handleShift} = this.getHandleParams();

            // $FlowFixMe TODO: computed property. Indexable signature not found in
            const mousePosition = e[pageAxis] - this.scrollLine.getBoundingClientRect()[indent];

            const handleCenter = handleSize / 2 + handleShift;
            const delta = mousePosition - handleCenter;
            this.onUpdateData(delta, false);
        }
    };

    onUpdateData(delta: number, dragging: boolean) {
        const {lineSize} = this.state;
        const {contentSize, containerSize, shift} = this.props;
        const {handleSize, handleShift} = this.getHandleParams();

        let updatedScrollableShift;
        if (handleSize > SCROLL_HANDLE_MIN_SIZE) {
            updatedScrollableShift = contentSize * (handleShift + delta) / lineSize;
        } else {
            updatedScrollableShift = (contentSize - containerSize) * (handleShift + delta) / (lineSize - handleSize);
        }
        const updatedScrollableDelta = Math.abs(shift) - updatedScrollableShift;

        this.props.updateData(updatedScrollableDelta, dragging);
    }

    getHandleParams() {
        const {lineSize} = this.state;
        const {containerSize, contentSize, shift} = this.props;

        let handleSize = lineSize * containerSize / contentSize;
        let handleShift = lineSize * Math.abs(shift) / contentSize;

        if (handleSize < SCROLL_HANDLE_MIN_SIZE) {
            handleSize = SCROLL_HANDLE_MIN_SIZE;
            handleShift = Math.abs(shift) * (lineSize - handleSize) / (contentSize - containerSize);
        }

        return {handleSize, handleShift};
    }

    onRef = (el: ?HTMLDivElement) => {
        this.scrollLine = el;
        this.nodeRef.current = el;
    };

    render() {
        const {direction, id, type, show} = this.props;
        const {size, indent} = SETTINGS[direction];
        const {handleSize, handleShift} = this.getHandleParams();

        const handleStyles = {
            [size]: handleSize + 'px',
            [indent]: handleShift + 'px'
        };

        const scrollClasses = classNames('scroll disable-selection',
            STYLE_CLASSES[type], {
                scroll_horizontal: direction !== Direction.VERTICAL,
                scroll_vertical: direction === Direction.VERTICAL
            });

        const scroll = (
            <div
                ref={this.onRef}
                className={scrollClasses}
                onClick={this.onLineClick}
                style={{opacity: show ? 1 : 0}}>
                <div
                    style={handleStyles}
                    className={classes.scroll__handle}
                    onClick={this.onHandleClick}
                />
            </div>
        );

        const content = (
            <DraggableCore
                nodeRef={this.nodeRef}
                onStart={this.onStart}
                onDrag={this.onDrag}
                onStop={this.onStop}
                handle={'.scroll__handle'}
                enableUserSelectHack={false}>
                {scroll}
            </DraggableCore>
        );

        return id ? <div id={id}>{content}</div> : content;
    }
}
